import React, { useMemo, useEffect, useState } from 'react';
import cx from 'classnames';
import Image from 'next/image';
import { Skeleton, useTheme } from '@mui/material';
import { WindArrowIcon } from '@wavetrak/theme';

import ErrorBoundary from 'components/ErrorBoundary';
import WavetrakLink from 'components/WavetrakLink/WavetrakLink';
import type { GeoLocation } from 'types/locationView';
import closestEquivalentAngle from 'utils/closestEquivalentAngle';
import { getSpotMapPathWithLocation, getWindStationMapPathWithLocation } from 'utils/urls/mapPaths';
import getMapImageSrc from 'utils/urls/mapTilerPath';

import Compass from './Compass';

import styles from './CurrentWindMap.module.scss';

export interface CurrentWindMapProps {
  changeAnimation?: boolean;
  geo?: GeoLocation;
  isError?: boolean;
  isLiveAvailable?: boolean;
  isLoading?: boolean;
  isTooltipMode?: boolean;
  loopAnimation?: boolean;
  mapZoom?: number;
  size?: 'small' | 'large';
  windDirection?: number;
  withSkeleton?: boolean;
}

const mapImageSrc = ({
  geo,
  isLarge,
  mapZoom,
}: {
  geo?: Partial<GeoLocation>;
  isLarge: boolean;
  mapZoom?: number;
}) =>
  getMapImageSrc({
    mapZoom: mapZoom || (isLarge ? 13.5 : 13),
    mapWidth: isLarge ? 1000 : 500,
    mapHeight: isLarge ? 700 : 350,
    geo,
  });

export const CurrentWindMap: React.FC<CurrentWindMapProps> = ({
  changeAnimation = false,
  geo,
  isError,
  isLiveAvailable = false,
  isLoading = false,
  isTooltipMode = false,
  loopAnimation = true,
  mapZoom,
  size = 'small',
  windDirection = 0,
  withSkeleton = false,
}) => {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);
  const isLarge = size === 'large';
  let showError = isError;

  if ((!geo?.lat || !geo?.lon) && !isLoading) {
    showError = true;
  }

  const location = `@${geo?.lat},${geo?.lon},13z`;
  const mapLinkHref =
    isLiveAvailable && geo
      ? getWindStationMapPathWithLocation(location)
      : getSpotMapPathWithLocation(location);

  const [rotation, setRotation] = useState(0);
  const arrowIconSize = useMemo(() => {
    if (isTooltipMode) {
      return 20;
    }
    if (isLarge) {
      return 55;
    }
    return 30;
  }, [isLarge, isTooltipMode]);

  const mapUrl = useMemo(() => mapImageSrc({ geo, isLarge, mapZoom }), [geo, isLarge, mapZoom]);

  useEffect(() => {
    setRotation((prev) => closestEquivalentAngle(prev, windDirection - 180));
  }, [windDirection]);

  return (
    <ErrorBoundary
      error={showError || imageError}
      errorBoundaryId="current-wind-map"
      render={() => (
        <figure
          className={cx(
            styles.map,
            isLarge && styles.mapLarge,
            !isLarge && styles.mapSmall,
            styles.mapError,
          )}
          data-testid="current-wind-map-error"
        />
      )}
    >
      {isLoading ? (
        <figure
          className={cx(
            styles.map,
            isLarge && styles.mapLarge,
            !isLarge && styles.mapSmall,
            styles.mapLoading,
            withSkeleton && styles.mapLoadingSkeleton,
          )}
          data-testid="current-wind-map-loading"
        >
          {withSkeleton && (
            <Skeleton
              animation="wave"
              variant="rectangular"
              width="100%"
              height="100%"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '>*': { visibility: 'unset' },
              }}
            >
              <Compass size={size} />
            </Skeleton>
          )}
        </figure>
      ) : (
        <WavetrakLink href={mapLinkHref}>
          <figure
            className={cx(
              isTooltipMode && styles.windTooltipMap,
              styles.map,
              isLarge && styles.mapLarge,
              !isLarge && styles.mapSmall,
            )}
          >
            <Image
              alt="Satellite map of location"
              data-testid="current-wind-map"
              data-src={mapUrl}
              layout="fill"
              loading="lazy"
              objectFit="cover"
              objectPosition="center"
              onError={() => setImageError(true)}
              placeholder={undefined}
              src={mapUrl}
              unoptimized
            />
            <figcaption className={styles.compass}>
              <Compass size={size} customSize={isTooltipMode ? 40 : null} />
              <div className={cx(styles.arrowContainer, 'sl-wind-map-arrow-container')}>
                <div
                  className={cx(
                    isLiveAvailable &&
                      loopAnimation &&
                      !changeAnimation &&
                      styles.arrowLoopAnimated,
                  )}
                  data-testid="current-wind-map-arrow-animation"
                >
                  <WindArrowIcon
                    rotation={rotation}
                    className={cx(styles.arrow, changeAnimation && styles.arrowChangeAnimated)}
                    color={theme.palette.common.white}
                    size={arrowIconSize}
                    aria-label={`Wind direction is ${windDirection} degrees`}
                    data-testid="current-wind-map-arrow"
                  />
                </div>
              </div>
            </figcaption>
          </figure>
        </WavetrakLink>
      )}
    </ErrorBoundary>
  );
};

export default CurrentWindMap;
